import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useImperativeHandle,
} from "react";
import { renderToStaticMarkup } from "react-dom/server";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import htmlDocx from "html-docx-js/dist/html-docx";
import { saveAs } from "file-saver";
import "./ChatInterface.css";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import AddIcon from "@mui/icons-material/Add";
import CampaignIcon from "@mui/icons-material/Campaign";
import NotificationsIcon from "@mui/icons-material/Notifications";
import DoneIcon from "@mui/icons-material/Done";
import SquareIcon from "@mui/icons-material/Square";
import { ClearIcon } from "@mui/x-date-pickers";
import blackcopychat from "../../../Assets/blackcopychat.png";
import blacktickchat from "../../../Assets/blacktickchat.png";
import whitecopychat from "../../../Assets/whitecopychat.png";
import whitetickchat from "../../../Assets/whitetickchat.png";
import atlaschatlogo from "../../../Assets/Atlas-Logo.svg";
import atlasPulsatingIcon from "../../../Assets/Atlas-pulsating-Icon.svg";
import { useConfirmNavigation } from "../useNavigationBlocker";
import useAtlasChatSocket from "../hooks/useAtlasChatSocket";
import useDeepInsights from "../hooks/useDeepInsights";
import AtlasChatFeedbackForm from "../feedback/AtlasChatFeedbackForm";
import useReportsMode from "../reports/useReportsMode";
import ReportsPopover from "../reports/ReportsPopover";
import FilterStep from "../reports/FilterStep";
import ReviewPromptStep from "../reports/ReviewPromptStep";
import PreviewStep from "../reports/PreviewStep";
import ScheduleStep from "../reports/ScheduleStep";
import { createSchedule } from "../reports/reportsApi";
import { toast } from "react-toastify";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import GoodFeedbackForm from "../feedback/GoodFeedbackForm";
import BadFeedbackForm from "../feedback/BadFeedbackForm";
import { FaRegThumbsUp } from "react-icons/fa6";
import { FaRegThumbsDown } from "react-icons/fa6";

const ChatInterface = React.forwardRef(
  (
    {
      userId,
      userProfileData,
      isMobile,
      isTabPortrait,
      isTabletLandscape,
      inputDisabled,
      setInputDisabled,
      setConnectionStatus,
      handleOpenDisclaimer,
      onNewChat,
    },
    ref,
  ) => {
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState("");
    const [pendingEditContent, setPendingEditContent] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
    const [isGoodFeedbackOpen, setIsGoodFeedbackOpen] = useState(false);
    const [isBadFeedbackOpen, setIsBadFeedbackOpen] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);
    const [openPopover, setOpenPopover] = useState(false);
    const [openLogPopup, setOpenLogPopup] = useState(false);
    const [selectedLog, setSelectedLog] = useState("");
    const inputRef = useRef(null);
    const chatBottomRef = useRef(null);
    const chatScrollRef = useRef(null);
    const popRef = useRef(null);
    const popupRef = useRef(null);
    const [showScrollButton, setShowScrollButton] = useState(false);

    const {
      deepInsights,
      setDeepInsights,
      logs,
      handleToggle,
      handleLogs,
      clearLogs,
    } = useDeepInsights();
    const reportUserEmail =
      userProfileData?.email ||
      userProfileData?.username ||
      localStorage.getItem("email") ||
      "";
    const reportUserName =
      [userProfileData?.firstName, userProfileData?.lastName]
        .filter(Boolean)
        .join(" ") ||
      userProfileData?.login ||
      "";
    const reports = useReportsMode({
      userEmail: reportUserEmail,
      // Review approved: send the prompt to the deep-insight agent. The report
      // preview streams back into the chat thread like a normal response.
      onSendToAgent: (prompt) => {
        console.log("[Reports] completed prompt:\n", prompt);
        setMessages((prev) => [
          ...prev,
          { content: prompt, sender: "user", timestamp: getTimeFormat() },
        ]);
        setIsLoading(true);
        setIsGenerating(true);
        setInputDisabled(true);
        sendMessage(prompt, { deepInsights: true });
        setTimeout(scrollToBottom, 100);
      },
      // Schedule confirmed: POST to the Create Schedule API. Returning the
      // promise lets the hook track submitting/error and close on success.
      onScheduleSubmit: async (schedule, prompt) => {
        await createSchedule({
          userId,
          userEmail: reportUserEmail,
          userName: reportUserName,
          userRequest: prompt,
          ...schedule,
        });
        toast.success("Report scheduled.");
      },
    });
    // Stable (useCallback) — safe to depend on directly in handleResponse.
    const { onPreviewResponse: advanceReportsPreview } = reports;
    const deepInsightsRef = useRef(deepInsights);
    useEffect(() => {
      deepInsightsRef.current = deepInsights;
    }, [deepInsights]);

    const getTimeFormat = () => {
      const now = new Date();
      const hours = now.getHours();
      const minutes = now.getMinutes();
      const ampm = hours >= 12 ? "PM" : "AM";
      const displayHours = hours % 12 || 12;
      return `${displayHours}:${minutes.toString().padStart(2, "0")} ${ampm}`;
    };

    const handleResponse = useCallback(
      (aiResponse) => {
        setMessages((prev) => [
          ...prev,
          {
            content: aiResponse,
            sender: "assistant",
            isMarkdown: true,
            timestamp: getTimeFormat(),
          },
        ]);
        clearLogs();
        setIsLoading(false);
        setIsGenerating(false);
        setInputDisabled(false);
        // Reports: a pending preview just landed — advance to the confirm step.
        advanceReportsPreview();
        if (pendingEditContent !== null) {
          setInputMessage(pendingEditContent);
          setPendingEditContent(null);
          setTimeout(() => inputRef.current?.focus(), 0);
        }
        setTimeout(
          () => chatBottomRef.current?.scrollIntoView({ behavior: "smooth" }),
          100,
        );
      },
      [clearLogs, pendingEditContent, setInputDisabled, advanceReportsPreview],
    );

    const { sendMessage, stop, reconnect, sessionIdRef, isConnected, error } =
      useAtlasChatSocket({
        userId,
        userProfileData,
        deepInsightsRef,
        onResponse: handleResponse,
        onToggle: handleToggle,
        onLogs: handleLogs,
        setInputDisabled,
      });
    useEffect(() => {
      if (typeof setConnectionStatus === "function") {
        setConnectionStatus(isConnected);
      }
    }, [isConnected, setConnectionStatus]);

    const scrollToBottom = () => {
      chatBottomRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
      scrollToBottom();
    }, [messages, isLoading]);

    useEffect(() => {
      const container = chatScrollRef.current;
      if (!container) return;
      const handleScroll = () => {
        const distanceFromBottom =
          container.scrollHeight - container.scrollTop - container.clientHeight;
        setShowScrollButton(distanceFromBottom > 100);
      };
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
      const closeOnClickOutside = (e) => {
        if (popRef.current && !popRef.current.contains(e.target))
          setOpenPopover(false);
      };
      document.addEventListener("mousedown", closeOnClickOutside);
      return () =>
        document.removeEventListener("mousedown", closeOnClickOutside);
    }, []);

    useEffect(() => {
      const handleClickOutside = (e) => {
        if (popupRef.current && !popupRef.current.contains(e.target))
          setOpenLogPopup(false);
      };
      if (openLogPopup)
        document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, [openLogPopup]);

    useConfirmNavigation(
      inputDisabled,
      "Leaving this chat will cancel report generation. Are you sure you want to exit?",
    );

    useEffect(() => {
      const handleBeforeUnload = (e) => {
        if (inputDisabled) {
          e.preventDefault();
          e.returnValue =
            "Leaving this chat will cancel report generation. Are you sure you want to exit?";
        }
      };
      window.addEventListener("beforeunload", handleBeforeUnload);
      return () =>
        window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [inputDisabled]);

    const handleSendMessage = () => {
      if (!inputMessage.trim() || inputDisabled) return;
      setMessages((prev) => [
        ...prev,
        {
          content: inputMessage,
          sender: "user",
          timestamp: getTimeFormat(),
        },
      ]);
      setIsLoading(true);
      setIsGenerating(true);
      setInputDisabled(true);
      sendMessage(inputMessage);
      setInputMessage("");
      setTimeout(scrollToBottom, 100);
    };

    const handleEnterKey = (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    };

    const handleStopButton = () => {
      stop();
      setIsGenerating(false);
      setIsLoading(false);
      setInputDisabled(false);
      clearLogs();
    };

    const handleNewChatInternal = () => {
      setMessages([]);
      clearLogs();
      setDeepInsights(false);
      setInputMessage("");
      setIsLoading(false);
      setIsGenerating(false);
      reconnect();
      if (onNewChat) onNewChat();
    };

    useImperativeHandle(ref, () => ({ newChat: handleNewChatInternal }));

    const handleCopy = (text, index) => {
      navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 1000);
    };

    const generateFilename = (content) => {
      // Extract first heading or first meaningful line for a descriptive filename
      const lines = content
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean);

      let title = "";
      for (const line of lines) {
        // Prefer headings
        const headingMatch = line.match(/^#{1,3}\s+(.+)/);
        if (headingMatch) {
          title = headingMatch[1];
          break;
        }
        // Fall back to first non-empty line that's reasonably descriptive
        if (!title && line.length > 5 && !line.startsWith("```")) {
          title = line;
          break;
        }
      }

      if (!title) title = "AtlasChat-Report";

      // Sanitize: remove markdown, special chars, truncate
      const sanitized = title
        .replace(/[*_`~[\]()>]/g, "") // strip markdown syntax
        .replace(/[^a-zA-Z0-9\s-]/g, "") // keep alphanumeric, spaces, hyphens
        .trim()
        .replace(/\s+/g, "-") // spaces → hyphens
        .slice(0, 60); // max 60 chars

      return `AtlasChat-${sanitized}.docx`;
    };

    const DISCLAIMER_HTML = `
  <div style="
    border: 1px solid #ccc;
    background-color: #f9f9f9;
    padding: 10px 14px;
    margin-bottom: 20px;
    font-size: 12px;
    color: #555;
    border-radius: 4px;
  ">
    <strong>Disclaimer:</strong> This report was generated by Atlas Chat. 
  </div>
`;

    const handleDownloadDocx = (content) => {
      const html = renderToStaticMarkup(
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>,
      );

      const htmlTemplate = `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8">
    <title>Atlas Chat Report</title>
    <style>
      body { font-family: Arial, sans-serif; padding: 20px; font-size: 14px; line-height: 1.6; color: #222; }
      h1, h2, h3 { color: #17202e; }
      p { margin: 0 0 8px; }
      ul, ol { padding-left: 20px; margin: 0 0 8px; }
      li { margin-bottom: 4px; }
      table { width: 100%; border-collapse: collapse; table-layout: fixed; margin: 8px 0; }
      th { background-color: #f2f2f2; font-weight: bold; }
      th, td { border: 1px solid #999; padding: 6px; font-size: 12px; word-break: break-word; }
      tr { page-break-inside: avoid; }
      a { color: blue; }
      code { background: #f3f4f6; padding: 2px 4px; font-size: 13px; }
      pre { background: #1e2230; color: #e2e8f0; padding: 12px; border-radius: 4px; }
    </style>
  </head>
  <body>
    ${DISCLAIMER_HTML}
    ${html}
  </body>
</html>`;

      const filename = generateFilename(content);
      const blob = htmlDocx.asBlob(htmlTemplate);
      saveAs(blob, filename);
    };

    const handleToggleDeepInsights = () => {
      const newValue = !deepInsights;
      setDeepInsights(newValue);
      setOpenPopover(false);
    };

    const isEmpty = !messages.length && logs.length === 0;

    return (
      <div
        className={
          isEmpty && isMobile
            ? "atlas-chat-main-start-inner-new"
            : "atlas-chat-main-start-inner"
        }
      >
        <div className="chat-main-ai-inner">
          {/* Research logs panel */}
          <div
            className={
              logs.length === 0
                ? "research-main-container-none"
                : "research-main-container"
            }
          >
            <div className="research-tasks-wrapper">
              {logs.length > 0 && (
                <div className="research-tasks-container">
                  <h2 className="research-tasks-heading">Research Tasks</h2>
                  <div className="research-tasks-list">
                    {logs.map((log, index) => {
                      const words = log.content?.split(" ") || [];
                      const short = words.slice(0, 4).join(" ");
                      const isLong = words.length > 4;
                      return (
                        <div key={index} className="research-task-item">
                          <span className="task-label task-label--completed">
                            • {short}
                            {isLong && (
                              <span
                                className="read-more-link"
                                onClick={() => {
                                  setSelectedLog(log.content);
                                  setOpenLogPopup(true);
                                }}
                              >
                                Read more
                              </span>
                            )}
                          </span>
                        </div>
                      );
                    })}

                    {openLogPopup && (
                      <div className="log-popup-overlay">
                        <div className="log-popup-box" ref={popupRef}>
                          <p className="ClearIcon-cross-deep">
                            <ClearIcon onClick={() => setOpenLogPopup(false)} />
                          </p>
                          <h3 className="full-log-details">Full Log Details</h3>
                          <p className="full-det-deep">{selectedLog}</p>
                          <div className="close-research-modal-deep">
                            <button
                              className="close-popup-btn"
                              onClick={() => setOpenLogPopup(false)}
                            >
                              Close
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Chat messages */}
          <div
            className={
              logs.length === 0
                ? "chat-container-ai-inner-none"
                : "chat-container-ai-inner"
            }
          >
            <div className="chat-messages-ai" ref={chatScrollRef}>
              {messages.map((msg, index) => (
                <React.Fragment key={index}>
                  <div className={`message-think ${msg.sender}`}>
                    <div
                      className={
                        msg.sender === "user"
                          ? "message-content-chat"
                          : "message-content-chat-assist-deep"
                      }
                    >
                      <div className="copy-res-deep">
                        {msg.sender === "assistant" ? (
                          <>
                            <FaRegThumbsUp
                              className="thumbs-up-icon-altas"
                              onClick={() => setIsGoodFeedbackOpen(true)}
                            />{" "}
                            <FaRegThumbsDown
                              className="thumbs-down-icon-altas"
                              onClick={() => setIsBadFeedbackOpen(true)}
                            />
                            <p
                              onClick={() => handleDownloadDocx(msg.content)}
                              className="download-docx-btn"
                            >
                              <FileDownloadIcon fontSize="small" />
                            </p>
                            <p onClick={() => handleCopy(msg.content, index)}>
                              <img
                                src={
                                  copiedIndex === index
                                    ? blacktickchat
                                    : blackcopychat
                                }
                                alt="copy"
                              />
                            </p>
                            <span className="timestamp-assist-atlas">
                              {msg.timestamp}
                            </span>{" "}
                          </>
                        ) : (
                          <>
                            <EditIcon
                              className="edit-icon-chat-altas"
                              onClick={() => {
                                if (isLoading || inputDisabled) {
                                  setPendingEditContent(msg.content);
                                  return;
                                }
                                setInputMessage(msg.content);
                                setTimeout(() => inputRef.current?.focus(), 0);
                                setTimeout(
                                  () =>
                                    chatBottomRef.current?.scrollIntoView({
                                      behavior: "smooth",
                                    }),
                                  100,
                                );
                              }}
                            />{" "}
                            <RefreshIcon
                              className="refresh-icon-chat-altas"
                              onClick={() => {
                                if (isLoading || inputDisabled) return;
                                setMessages((prev) => [
                                  ...prev,
                                  {
                                    content: msg.content,
                                    sender: "user",
                                    timestamp: getTimeFormat(),
                                  },
                                ]);
                                setIsLoading(true);
                                setIsGenerating(true);
                                setInputDisabled(true);
                                sendMessage(msg.content);
                                setInputMessage("");
                                setTimeout(scrollToBottom, 100);
                              }}
                            />{" "}
                            <p onClick={() => handleCopy(msg.content, index)}>
                              <img
                                src={
                                  copiedIndex === index
                                    ? whitetickchat
                                    : whitecopychat
                                }
                                alt="copy"
                              />
                            </p>
                            <span className="timestamp-assist-atlas">
                              {msg.timestamp}
                            </span>{" "}
                          </>
                        )}
                      </div>
                      <div
                        className={
                          msg.sender === "user"
                            ? "message-text-chat"
                            : "message-text-chat-assist"
                        }
                      >
                        {msg.isMarkdown ? (
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm, remarkBreaks]}
                            components={{
                              a: ({ href, children }) => {
                                const text =
                                  typeof children === "string" &&
                                  children.startsWith("mailto:")
                                    ? children.slice(7)
                                    : children;
                                return (
                                  <a
                                    href={href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {text}
                                  </a>
                                );
                              },
                              table: ({ children }) => (
                                <div className="table-box">
                                  <div className="table-box-scroll">
                                    <table>{children}</table>
                                  </div>
                                </div>
                              ),
                            }}
                          >
                            {msg.content}
                          </ReactMarkdown>
                        ) : (
                          msg.content
                        )}
                      </div>
                    </div>
                  </div>

                  {msg.sender === "assistant" &&
                    index === messages.length - 1 && (
                      <div className="atlas-icon-static">
                        <img src={atlasPulsatingIcon} alt="Atlas" />
                      </div>
                    )}
                </React.Fragment>
              ))}

              {isLoading && (
                <div className="typing-message">
                  <div className="message-content">
                    <img
                      src={atlasPulsatingIcon}
                      alt="Atlas thinking"
                      className="atlas-icon-pulsating"
                    />
                    <div className="typing-indicator">
                      Thinking
                      <span />
                      <span />
                      <span />
                    </div>
                  </div>
                </div>
              )}

              {isEmpty && (
                <div className="property-card-main-chat">
                  <img src={atlaschatlogo} alt="Atlas Chat" />
                  <p className="how-can-help-joi">How can I help you today?</p>
                </div>
              )}

              <div ref={chatBottomRef} />
            </div>
          </div>
        </div>

        {showScrollButton && (
          <button
            className="scroll-to-bottom-btn"
            onClick={() =>
              chatBottomRef.current?.scrollIntoView({ behavior: "smooth" })
            }
          >
            ↓
          </button>
        )}

        {/* Input area */}
        <div className="chat-input-container">
          <div className="chat-input-wrapper">
            <button
              className="feedback-fab"
              onClick={() => setIsFeedbackOpen(true)}
            >
              <i className="fa-solid fa-circle-exclamation"></i>
              <span>Feedback</span>
            </button>

            <AtlasChatFeedbackForm
              open={isFeedbackOpen}
              onClose={() => setIsFeedbackOpen(false)}
              sessionId={sessionIdRef.current}
            />

            <div className="chat-input-form">
              <div className="text-area-property-match">
                <div className="text-area-inner">
                  <div className="chatgpt-plus-wrapper" ref={popRef}>
                    <button
                      disabled={Boolean(inputDisabled)}
                      className="chatgpt-plus-btn"
                      onClick={() => setOpenPopover(!openPopover)}
                    >
                      <span className="deep-icon-activate">
                        <AddIcon
                          fontSize="small"
                          className={deepInsights ? "icon-deep-active" : ""}
                        />
                      </span>
                    </button>

                    {openPopover && (
                      <div className="chatgpt-plus-popover">
                        {!deepInsights ? (
                          <div
                            className="popover-item"
                            onClick={handleToggleDeepInsights}
                          >
                            <CampaignIcon /> Deep Insights Mode
                          </div>
                        ) : (
                          <div
                            className="popover-item-activated"
                            onClick={handleToggleDeepInsights}
                          >
                            <CampaignIcon /> Deep Insights Mode <DoneIcon />
                          </div>
                        )}
                        <div
                          className="popover-item"
                          onClick={() => {
                            setOpenPopover(false);
                            reports.open();
                          }}
                        >
                          <NotificationsIcon /> Reports On The Go
                        </div>
                      </div>
                    )}

                    <ReportsPopover
                      open={reports.isOpen}
                      onClose={reports.cancel}
                      title={
                        reports.state === "reviewing"
                          ? "Review prompt"
                          : reports.state === "previewing"
                          ? "Generating preview"
                          : reports.state === "confirming"
                          ? "Confirm report"
                          : reports.state === "scheduling"
                          ? "Schedule report"
                          : "Create Sector Focused Email Report"
                      }
                      footer={
                        reports.state === "reviewing" ? (
                          <>
                            <button
                              type="button"
                              className="reports-btn"
                              onClick={reports.backToFilter}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="reports-btn reports-btn--primary"
                              onClick={reports.approvePrompt}
                            >
                              Approve
                            </button>
                          </>
                        ) : reports.state === "previewing" ? (
                          <button
                            type="button"
                            className="reports-btn"
                            onClick={reports.cancel}
                          >
                            Cancel
                          </button>
                        ) : reports.state === "confirming" ? (
                          <>
                            <button
                              type="button"
                              className="reports-btn"
                              onClick={reports.backToFilter}
                            >
                              Reject
                            </button>
                            <button
                              type="button"
                              className="reports-btn reports-btn--primary"
                              onClick={reports.confirmPreview}
                            >
                              Confirm
                            </button>
                          </>
                        ) : reports.state === "scheduling" ? (
                          <>
                            <button
                              type="button"
                              className="reports-btn"
                              onClick={reports.cancel}
                              disabled={reports.scheduleSubmitting}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              className="reports-btn reports-btn--primary"
                              onClick={reports.submitSchedule}
                              disabled={
                                !reports.canSchedule || reports.scheduleSubmitting
                              }
                            >
                              {reports.scheduleSubmitting ? "Scheduling…" : "Schedule"}
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              className="reports-btn"
                              onClick={reports.cancel}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              className="reports-btn reports-btn--primary"
                              disabled={!reports.canProceed}
                              onClick={reports.proceedToReview}
                            >
                              Continue
                            </button>
                          </>
                        )
                      }
                    >
                      {reports.state === "reviewing" ? (
                        <ReviewPromptStep prompt={reports.generatedPrompt} />
                      ) : reports.state === "previewing" ? (
                        <PreviewStep ready={false} />
                      ) : reports.state === "confirming" ? (
                        <PreviewStep ready />
                      ) : reports.state === "scheduling" ? (
                        <ScheduleStep
                          schedule={reports.schedule}
                          updateSchedule={reports.updateSchedule}
                          submitting={reports.scheduleSubmitting}
                          error={reports.scheduleError}
                        />
                      ) : (
                        <FilterStep
                          filters={reports.filters}
                          toggleFilter={reports.toggleFilter}
                          setContext={reports.setContext}
                        />
                      )}
                    </ReportsPopover>
                  </div>
                  {deepInsights && (
                    <span className="deep-insights-chip">
                      <span>Deep Insights</span>
                      <button
                        className="deep-insights-chip-close"
                        onClick={handleToggleDeepInsights}
                      >
                        ×
                      </button>
                    </span>
                  )}
                  {reports.isOpen && (
                    <span className="deep-insights-chip">
                      <span>Reports On The Go</span>
                      <button
                        className="deep-insights-chip-close"
                        onClick={reports.cancel}
                      >
                        ×
                      </button>
                    </span>
                  )}
                  <textarea
                    ref={inputRef}
                    className={`chat-box-property-match${deepInsights ? " chat-box-property-match--deep" : ""}${reports.isOpen ? " chat-box-property-match--reports" : ""}`}
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyDown={handleEnterKey}
                    placeholder="Message Atlas Chat"
                    rows={1}
                    disabled={Boolean(inputDisabled)}
                  />
                  {inputDisabled ? (
                    <button
                      type="button"
                      className="textarea-upload-button"
                      onClick={handleStopButton}
                    >
                      <SquareIcon className="stop-icon" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="textarea-upload-button"
                      onClick={handleSendMessage}
                      disabled={Boolean(inputDisabled)}
                    >
                      <ArrowUpwardIcon
                        className={deepInsights ? "icon-deep-active" : ""}
                      />
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="input-help">
              {error && (
                <p className="disc-atlas-chat ac-error" 
                >
                  {error}
                </p>
              )}
              {!isConnected && !error && (
                <p className="disc-atlas-chat ac-connecting" 
                >
                  Connecting to Atlas Chat...
                </p>
              )}
              <p className="disc-atlas-chat">
                <span className="disc-atlas-chat-bold"> Disclaimer:</span>{" "}
                Information is for guidance only and should not be relied upon
                without source and human validation.
              </p>
              <p className="disc-atlas-chat">
                Refer to the{" "}
                <span
                  className="disclamer-footer-jo"
                  onClick={handleOpenDisclaimer}
                >
                  disclaimers
                </span>{" "}
                when using Atlas Chat.
              </p>
            </div>
          </div>
        </div>

        <div>
          <GoodFeedbackForm
            open={isGoodFeedbackOpen}
            onClose={() => setIsGoodFeedbackOpen(false)}
            sessionId={sessionIdRef.current}
          />
          <BadFeedbackForm
            open={isBadFeedbackOpen}
            onClose={() => setIsBadFeedbackOpen(false)}
            sessionId={sessionIdRef.current}
          />
        </div>
      </div>
    );
  },
);

export default ChatInterface;
